import React, {
  forwardRef,
  useCallback,
  useContext,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';
import { FormApi } from 'final-form';
import { t } from 'i18next';
import validations from 'validations';
import { Button } from 'ncoded-component-library';
import { Form, Field } from 'react-final-form';
import SideDrawer from '../../../SideDrawer';
import InputField from 'components/InputField';
import RadioGroupField from 'components/RadioGroupField';
import QuestionRichTextEditor from '../../../QuestionRichTextEditor';
import ChooseAFileInDr from '../../../ChooseAFileInDr';
import {
  QAndASides,
  Question,
  QuestionFile,
} from 'router/subrouters/DataRoom/models/QAndA';
import PopNotificationsContext from 'providers/PopNotifications/PopNotifications.context';
import DataRoomContext from 'router/subrouters/DataRoom/providers/DataRoom/DataRoom.context';
import { DataRoomNode } from 'router/subrouters/DataRoom/models/DataRoomNode';
import { SideDrawerRef } from '../../../SideDrawer/SideDrawer.component';
import RejectedQuestionBanner from '../RejectedQuestionBanner';
import { isBuyManageRole } from 'router/subrouters/DataRoom/utils';
import SelectField from 'components/SelectField';
import useAskQuestionDrawer from 'router/subrouters/DataRoom/hooks/useAskQuestionDrawerCommon';
import useCallbackRef from 'hooks/useCallbackRef';
import arrayMutators from 'final-form-arrays';
import useQuestionsPriority from 'router/subrouters/DataRoom/hooks/useQuestionsPriority';

import './AskQuestionDrawer.styles.scss';

type AskQuestionDrawerProps = {
  selectedForQAndADrawer?: DataRoomNode;
  initialValues?: Partial<Question>;
  title?: string;
  deleteButton?: React.ReactNode;
  rejectedBySide?: QAndASides;
  rejectQuestionRespond?: string;
  questionId?: string;
  files?: QuestionFile[];
  onSubmit: (values: Question, node: string) => Promise<void>;
  onClose?: () => void;
  onRemoveQuestionFile?: (fileId: string) => void;
};

const AskQuestionDrawer: React.ForwardRefRenderFunction<
  SideDrawerRef,
  AskQuestionDrawerProps
> = (
  {
    selectedForQAndADrawer,
    title,
    deleteButton,
    initialValues,
    rejectedBySide,
    rejectQuestionRespond,
    questionId,
    files,
    onSubmit,
    onClose,
    onRemoveQuestionFile,
  },
  ref,
) => {
  const [drawer, drawerRef] = useCallbackRef<SideDrawerRef>(null);

  const chooseAFileInDrRef = useRef(null);

  const [nodeToRelateTo, setNodeToRelateTo] = useState<DataRoomNode>(null);

  const {
    qaRole,
    dataRoom: { _id: dataRoomId },
  } = useContext(DataRoomContext);
  const { popServerError } = useContext(PopNotificationsContext);

  useImperativeHandle(ref, () => drawer, [drawer]);

  const questionPriorityOptions = useQuestionsPriority();

  const submitText = useMemo(() => {
    if (isBuyManageRole(qaRole)) return 'submitQuestionToSellSide';

    if (rejectedBySide) return 'updateQuestionForReview';

    return 'submitQuestionForReview';
  }, [qaRole, rejectedBySide]);

  const { questionSubjectsOptions, successNotification } =
    useAskQuestionDrawer();

  const onQuestionSubmit = useCallback(
    async (values: Question, formApi: FormApi<Question, Partial<Question>>) => {
      try {
        await onSubmit(values, nodeToRelateTo?._id);
        onClose?.();
        successNotification();
        formApi.restart();
      } catch (e) {
        popServerError(t('qaError'));
      }
    },
    [nodeToRelateTo, onClose, onSubmit, popServerError, successNotification],
  );

  useEffect(() => {
    selectedForQAndADrawer && setNodeToRelateTo(selectedForQAndADrawer);
  }, [selectedForQAndADrawer]);

  return (
    <SideDrawer
      className="qa-ask-question-drawer"
      ref={drawerRef}
      closeAction={() => {
        onClose?.();
        chooseAFileInDrRef.current?.setIsEditing(false);
        setNodeToRelateTo(null);
      }}
    >
      <p className="drawer-title">{title}</p>
      {rejectedBySide && (
        <RejectedQuestionBanner
          side={rejectedBySide}
          rejectQuestionRespond={rejectQuestionRespond}
        />
      )}
      {drawer?.open && (
        <Form
          mutators={{ ...arrayMutators }}
          initialValues={initialValues}
          onSubmit={onQuestionSubmit}
          render={({ handleSubmit, submitting, invalid, pristine, values }) => {
            return (
              <form onSubmit={handleSubmit}>
                <Field
                  name="subject"
                  label={t('questionSubject')}
                  component={SelectField}
                  options={questionSubjectsOptions}
                  searchable
                  required
                  key={values.subject}
                  validate={validations.required()}
                />
                {values.subject === 'Other' && (
                  <Field
                    name="otherSubject"
                    label={t('otherSubject')}
                    component={InputField}
                    maxLength={100}
                    validate={validations.required()}
                    required
                  />
                )}

                <QuestionRichTextEditor
                  label={t('writeBriefQuestion')}
                  questionId={questionId}
                  initialFiles={files}
                  onRemoveQuestionFile={onRemoveQuestionFile}
                />

                <ChooseAFileInDr
                  className="qa-ask-question-drawer__file"
                  dataRoomId={dataRoomId}
                  currenntlySelectedFile={nodeToRelateTo}
                  onChange={setNodeToRelateTo}
                  ref={chooseAFileInDrRef}
                />

                <RadioGroupField
                  required
                  options={questionPriorityOptions}
                  name="priority"
                  label={t('priority')}
                  direction="row"
                />
                <div className="qa-manage-question-buy__form__actions">
                  <Button
                    type="submit"
                    disabled={submitting || invalid || pristine}
                  >
                    {t(submitText)}
                  </Button>
                  {deleteButton}
                </div>
              </form>
            );
          }}
        />
      )}
    </SideDrawer>
  );
};

export default forwardRef(AskQuestionDrawer);
